import React, { useEffect, useState } from "react";
import staffApi from "@/lib/staffApi";
import { PageHead } from "./DashboardLayout";
import { useStaff } from "@/context/StaffContext";
import { money, DEPARTMENTS, COLLECTIONS } from "@/data/site";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Upload } from "lucide-react";

const EMPTY = { name: "", department: "men", collection: "essentials", category: "Hoodies", description: "", badge: "", price: 100, previous_price: "", colors: ["Black"], sizes: ["S", "M", "L", "XL"], images: [], featured: false, is_new: true };

export default function AdminProducts() {
  const { can } = useStaff();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const fetchData = () => staffApi.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { fetchData(); }, []);

  const archive = async (p) => {
    try { const { data } = await staffApi.post(`/admin/products/${p.id}/archive`); toast.success(data.archived ? "Archived" : "Restored"); fetchData(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-products-page">
      <PageHead kicker="Catalog" title="Products"
        action={can("admin") && <button onClick={() => setEditing(EMPTY)} data-testid="new-product-button" className="flex items-center gap-2 bg-gold px-5 py-3 text-xs font-bold uppercase tracking-widest text-black hover:bg-gold-bright"><Plus size={14} /> New Product</button>} />

      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[640px] text-sm" data-testid="products-table">
          <thead><tr className="bg-ink text-gold/80">{["", "Name", "Dept", "Collection", "Price", "Stock", "Status", ""].map((h, i) => <th key={i} className="px-4 py-3 text-left font-mono text-[0.65rem] uppercase tracking-widest">{h}</th>)}</tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-white/10 text-cream/80" data-testid={`product-row-${p.slug}`}>
                <td className="px-4 py-2"><img src={p.images?.[0]} alt={p.name} className="h-12 w-9 object-cover border border-white/10" /></td>
                <td className="px-4 py-3 text-cream">{p.name}</td>
                <td className="px-4 py-3 capitalize">{p.department}</td>
                <td className="px-4 py-3 capitalize">{p.collection}</td>
                <td className="px-4 py-3 font-mono">{money(p.price)}</td>
                <td className="px-4 py-3 font-mono">{p.total_stock}</td>
                <td className="px-4 py-3">{p.archived ? <span className="text-red-400">Archived</span> : <span className="text-green-400">Active</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    {can("admin") && <button onClick={() => setEditing({ ...p, previous_price: p.previous_price || "", badge: p.badge || "" })} data-testid={`edit-${p.slug}`} className="border border-white/15 px-3 py-1.5 text-[0.6rem] uppercase tracking-widest hover:border-gold hover:text-gold">Edit</button>}
                    {can("super") && <button onClick={() => archive(p)} data-testid={`archive-${p.slug}`} className="border border-white/15 px-3 py-1.5 text-[0.6rem] uppercase tracking-widest hover:border-red-400 hover:text-red-400">{p.archived ? "Restore" : "Archive"}</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <ProductModal initial={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); fetchData(); }} />}
    </div>
  );
}

function ProductModal({ initial, onClose, onDone }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isEdit = !!form.id;

  const imgList = Array.isArray(form.images) ? form.images : (form.images ? String(form.images).split(",").map((s) => s.trim()).filter(Boolean) : []);
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await staffApi.post("/admin/uploads", fd);
      setForm((prev) => ({ ...prev, images: [...imgList, ...data.urls] }));
      toast.success(`${data.urls.length} image(s) uploaded`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeImage = (i) => setForm((prev) => ({ ...prev, images: imgList.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    const payload = {
      name: form.name, department: form.department, collection: form.collection, category: form.category,
      description: form.description, badge: form.badge || null, price: Number(form.price),
      previous_price: form.previous_price ? Number(form.previous_price) : null,
      colors: typeof form.colors === "string" ? form.colors.split(",").map((s) => s.trim()).filter(Boolean) : form.colors,
      sizes: typeof form.sizes === "string" ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : form.sizes,
      images: typeof form.images === "string" ? form.images.split(",").map((s) => s.trim()).filter(Boolean) : form.images,
      featured: !!form.featured, is_new: !!form.is_new,
    };
    if (!payload.images.length) { toast.error("Please upload at least one product image"); setLoading(false); return; }
    try {
      if (isEdit) await staffApi.put(`/admin/products/${form.id}`, payload);
      else await staffApi.post("/admin/products", payload);
      toast.success(isEdit ? "Product updated" : "Product created");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const asStr = (v) => (Array.isArray(v) ? v.join(", ") : v);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="product-modal">
      <div className="absolute inset-0 bg-black/85" onClick={onClose} />
      <form onSubmit={submit} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto border border-gold/25 bg-ink p-6">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-serif text-xl text-cream">{isEdit ? "Edit Product" : "New Product"}</h3>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} className="text-cream/60" /></button>
        </div>
        <F label="Name" value={form.name} onChange={set("name")} testid="pf-name" />
        <div className="grid grid-cols-2 gap-3">
          <Sel label="Department" value={form.department} onChange={set("department")} options={DEPARTMENTS.map((d) => [d.key, d.label])} testid="pf-department" />
          <Sel label="Collection" value={form.collection} onChange={set("collection")} options={COLLECTIONS.map((c) => [c.key, c.label])} testid="pf-collection" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Category" value={form.category} onChange={set("category")} testid="pf-category" />
          <F label="Badge (optional)" value={form.badge} onChange={set("badge")} required={false} testid="pf-badge" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Price" type="number" value={form.price} onChange={set("price")} testid="pf-price" />
          <F label="Compare price" type="number" value={form.previous_price} onChange={set("previous_price")} required={false} testid="pf-prevprice" />
        </div>
        <F label="Colours (comma separated)" value={asStr(form.colors)} onChange={set("colors")} testid="pf-colors" />
        <F label="Sizes (comma separated)" value={asStr(form.sizes)} onChange={set("sizes")} testid="pf-sizes" />
        <div className="mb-3">
          <span className="overline-label mb-1 block text-[0.6rem]">Product Images</span>
          {imgList.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2" data-testid="pf-image-previews">
              {imgList.map((src, i) => (
                <div key={i} className="relative h-16 w-14 border border-white/15">
                  <img src={src} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} data-testid={`pf-image-remove-${i}`} aria-label="Remove image"
                    className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center border border-white/30 bg-black text-xs text-cream hover:text-gold">×</button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex cursor-pointer items-center gap-2 border border-gold/40 px-4 py-2 text-xs uppercase tracking-widest text-gold transition hover:bg-gold/10" data-testid="pf-upload-label">
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload images"}
            <input type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={onUpload} disabled={uploading} className="hidden" data-testid="pf-upload-input" />
          </label>
          <p className="mt-1 text-[0.65rem] text-cream/40">PNG, JPG or WEBP · up to 12MB each. First image is the main thumbnail.</p>
        </div>
        <label className="mb-3 block"><span className="overline-label mb-1 block text-[0.6rem]">Description</span>
          <textarea value={form.description} onChange={set("description")} rows={3} data-testid="pf-description" className="w-full border border-white/15 bg-black px-4 py-2 text-sm text-cream outline-none focus:border-gold" /></label>
        <div className="mb-5 flex gap-6 text-sm text-cream/70">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pf-featured" /> Featured</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} data-testid="pf-isnew" /> New Arrival</label>
        </div>
        <button disabled={loading} data-testid="product-save" className="w-full bg-gold py-3 text-xs font-bold uppercase tracking-widest text-black hover:bg-gold-bright disabled:opacity-50">{loading ? "Saving…" : isEdit ? "Save Changes" : "Create Product"}</button>
      </form>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required = true, testid }) {
  return <label className="mb-3 block"><span className="overline-label mb-1 block text-[0.6rem]">{label}</span>
    <input required={required} type={type} value={value} onChange={onChange} data-testid={testid} className="w-full border border-white/15 bg-black px-4 py-2 text-sm text-cream outline-none focus:border-gold" /></label>;
}
function Sel({ label, value, onChange, options, testid }) {
  return <label className="mb-3 block"><span className="overline-label mb-1 block text-[0.6rem]">{label}</span>
    <select value={value} onChange={onChange} data-testid={testid} className="w-full border border-white/15 bg-black px-4 py-2 text-sm text-cream outline-none focus:border-gold">
      {options.map(([v, l]) => <option key={v} value={v} className="bg-black">{l}</option>)}</select></label>;
}
